/**
 * Admin feature manifest — the single source of truth for both
 * the backend feature registry (`modules/admin/registry.ts`) and the
 * frontend admin sidebar (`packages/frontend-admin/src/components/shell/Sidebar.tsx`).
 *
 * Adding a feature is always: one row here + one re-export in the backend
 * registry. The sidebar never needs editing.
 */
import { AdminAction } from "./AdminAuthorization.js";
export var AdminFeatureSlug;
(function (AdminFeatureSlug) {
    /**
     * Platform usage & activity metrics dashboard. Deliberately the empty
     * string: the dashboard replaces the admin home page, so the sidebar/route
     * path derived as `/<slug>` resolves to `/` — no dedicated `/usage` route
     * exists. The backend API stem (`/admin/v1/usage/*`) is derived from the
     * backend module directory name (`modules/usage_metrics/`), independent of
     * this slug — same divergence precedent as PLATFORM_SUBSCRIPTIONS.
     */
    AdminFeatureSlug["USAGE"] = "";
    AdminFeatureSlug["ORGANIZATIONS"] = "organizations";
    /**
     * AI model catalog: the models the platform may call, their prices, and the
     * bindings that decide which model serves which task type. Route-level
     * permissions are split — the list and the price surface need
     * `MANAGE_PLATFORM_PRICING`, catalog lifecycle and bindings need
     * `MANAGE_AI_MODELS`.
     */
    AdminFeatureSlug["AI_MODELS"] = "ai-models";
    /**
     * Partnership-orgs surface: partner CRUD, code minting/revocation, org
     * enrollment, and bulk AI pause/resume. Full sidebar entry.
     *
     * Slug value governs only the SPA route + sidebar label — the backend
     * URL stem `/admin/v1/platform-subscriptions/partners/*` is derived
     * independently from the backend module directory name
     * (`modules/admin/modules/platform_subscriptions/`). The two can diverge
     * as they do here: SPA at `/partnerships`, API at `/platform-subscriptions`.
     */
    AdminFeatureSlug["PLATFORM_SUBSCRIPTIONS"] = "partnerships";
    /**
     * Finished-cattle quotes per UF as every organization sees them, and the
     * manual emergency quote an operator may enter when a feed is dead or wrong.
     * SPA route and API stem agree: `/commodity-prices`.
     */
    AdminFeatureSlug["COMMODITY_PRICES"] = "commodity-prices";
    /**
     * The support queue: every ticket tenants opened from the app's Help
     * section, with staff replies and status changes. SPA route and API stem
     * agree: `/support-tickets`.
     */
    AdminFeatureSlug["SUPPORT_TICKETS"] = "support-tickets";
    /**
     * Scheduled worker jobs: the EventBridge schedules, the run log read off
     * `scheduled_job_runs`, and the manual trigger. SPA route and API stem
     * agree: `/scheduled-jobs`.
     */
    AdminFeatureSlug["SCHEDULED_JOBS"] = "scheduled-jobs";
    /**
     * Message templates: the texts the platform sends to its users, per
     * channel, with fixed `{{variables}}`. SPA route and API stem agree:
     * `/message-templates`.
     */
    AdminFeatureSlug["MESSAGE_TEMPLATES"] = "message-templates";
    /**
     * Lifecycle emails: the four automatic account and trial emails, each bound
     * to a message template and switched on or off. SPA route and API stem
     * agree: `/lifecycle-emails`.
     */
    AdminFeatureSlug["LIFECYCLE_EMAILS"] = "lifecycle-emails";
    /**
     * The system cultivar catalog: the pasture cultivars every organization
     * picks from when composing a paddock, owned by the platform
     * (`grass_optz_cultivars.organizationId IS NULL`). An organization's own
     * cultivars are never reachable from here. SPA route and API stem agree:
     * `/cultivars`.
     */
    AdminFeatureSlug["CULTIVARS"] = "cultivars";
    /**
     * Soil interpretation references: the source-backed tables
     * (`grass_optz_soil_reference_profiles`) each paddock picks to classify its
     * soil analyses. SPA route and API stem agree: `/soil-references`.
     */
    AdminFeatureSlug["SOIL_REFERENCES"] = "soil-references";
    /** Admin-console access and future platform-level configuration. */
    AdminFeatureSlug["SETTINGS"] = "settings";
})(AdminFeatureSlug || (AdminFeatureSlug = {}));
export const ADMIN_FEATURE_MANIFESTS = [
    {
        slug: AdminFeatureSlug.USAGE,
        label: "Usage",
        description: "Platform activity and usage metrics dashboard.",
        order: 5,
    },
    {
        slug: AdminFeatureSlug.ORGANIZATIONS,
        label: "Organizations",
        description: "Cross-tenant list of organizations.",
        order: 10,
    },
    {
        slug: AdminFeatureSlug.PLATFORM_SUBSCRIPTIONS,
        label: "Partnerships",
        description: "Partner organizations, code minting, and enrollment for the Stripe-bypass plan.",
        order: 20,
    },
    {
        slug: AdminFeatureSlug.AI_MODELS,
        label: "AI Models",
        description: "Model catalog, effective-dated pricing, and the bindings that decide which model serves which task.",
        order: 30,
        // Deliberately the LESSER of the two permissions the surface uses, so a
        // billing operator still reaches the price screen. Catalog and binding
        // controls are gated route-by-route on MANAGE_AI_MODELS.
        permission: AdminAction.MANAGE_PLATFORM_PRICING,
    },
    {
        slug: AdminFeatureSlug.COMMODITY_PRICES,
        label: "Commodity prices",
        description: "Finished-cattle quotes per state as the product shows them, with a manual emergency quote per state.",
        order: 40,
        // The whole surface is one permission: reading which quote every tenant
        // sees is the first step of overriding it, and both are SUPER_ADMIN-only.
        permission: AdminAction.MANAGE_COMMODITY_PRICES,
    },
    {
        slug: AdminFeatureSlug.SUPPORT_TICKETS,
        label: "Support tickets",
        description: "Tickets tenants opened from the app's Help section: reply as staff and move them through their statuses.",
        order: 50,
        permission: AdminAction.MANAGE_SUPPORT_TICKETS,
    },
    {
        slug: AdminFeatureSlug.SCHEDULED_JOBS,
        label: "Scheduled jobs",
        description: "Every scheduled worker job: its EventBridge schedule, the latest runs with their errors, and a manual trigger.",
        order: 60,
        permission: AdminAction.VIEW_SCHEDULED_JOBS,
    },
    {
        slug: AdminFeatureSlug.MESSAGE_TEMPLATES,
        label: "Message templates",
        description: "The email and WhatsApp texts the platform sends to its users, with fixed variables filled in at send time.",
        order: 70,
        permission: AdminAction.MANAGE_MESSAGE_TEMPLATES,
    },
    {
        slug: AdminFeatureSlug.LIFECYCLE_EMAILS,
        label: "Lifecycle emails",
        description: "The four automatic account and trial emails: which message template each one uses and whether it is switched on.",
        order: 80,
        permission: AdminAction.MANAGE_LIFECYCLE_EMAILS,
    },
    {
        slug: AdminFeatureSlug.CULTIVARS,
        label: "Cultivars",
        description: "The system pasture-cultivar catalog every organization picks from: heights, rest windows, and whether a cultivar is still offered.",
        order: 90,
        permission: AdminAction.MANAGE_CULTIVARS,
    },
    {
        slug: AdminFeatureSlug.SOIL_REFERENCES,
        label: "Soil references",
        description: "The published soil-fertility tables paddocks pick to classify their soil analyses: each indicator's bands, class levels and colours.",
        order: 95,
        permission: AdminAction.MANAGE_SOIL_REFERENCES,
    },
    {
        slug: AdminFeatureSlug.SETTINGS,
        label: "Settings",
        description: "Admin-console access and configuration.",
        order: 100,
        permission: AdminAction.VIEW_ADMINS,
    },
];
//# sourceMappingURL=AdminFeature.js.map